import { Level } from '@wemap/geo';

// Hardcoded English locales based on /Users/thibaud/wemap/livemap/src/locales/en/index.ts
const LOCALES: Record<string, string> = {
    INSTRUCTION_DEPARTURE: 'Your position',
    INSTRUCTION_STRAIGHT: 'Keep going straight {street, select, undefined {} other {on {street}} }',
    INSTRUCTION_LAST_STEP: 'Your destination is {direction, select, right {on your right} left {on your left} other {{direction}} }',
    INSTRUCTION_IN_FRONT: 'in front',
    INSTRUCTION_LEFT: 'left',
    INSTRUCTION_RIGHT: 'right',
    INSTRUCTION_TURN: 'Turn {direction} {street, select, undefined {} other {on {street}} }',
    INSTRUCTION_TURN_SLIGHTLY: 'Turn slightly {direction} {street, select, undefined {} other {on {street}} }',
    INSTRUCTION_GATE: 'Take the gate {name, select, undefined {} other {{name}} }',
    INSTRUCTION_ENTRANCE: 'Use the entrance {name, select, undefined {} other {{name}} }',
    INSTRUCTION_EXIT: 'Take the exit {name, select, undefined {} other {{name}} }',
    INSTRUCTION_TURNSTILE: 'Pass through the access control.',
    INSTRUCTION_UP: 'Go up {street, select, undefined {} other {on {street}} }',
    INSTRUCTION_DOWN: 'Go down {street, select, undefined {} other {on {street}} }',
    INSTRUCTION_UP_STAIRS: 'Go up the stairs',
    INSTRUCTION_DOWN_STAIRS: 'Go down the stairs',
    INSTRUCTION_UP_ELEVATOR: 'Take the elevator',
    INSTRUCTION_DOWN_ELEVATOR: 'Take the elevator',
    INSTRUCTION_UP_ESCALATOR: 'Go up the escalator',
    INSTRUCTION_DOWN_ESCALATOR: 'Go down the escalator',
    INSTRUCTION_SUBWAY_EXIT: 'Take exit {exit}',
    AND: 'and',
};

// Simple i18n replacement function
function i18n(key: string, params?: Record<string, any>): string {
    let template = LOCALES[key] || key;
    
    if (params) {
        // Handle select statements (simplified)
        template = template.replace(/\{(\w+), select, [^}]+\}/g, (match, paramName) => {
            const value = params[paramName];
            if (value === undefined || value === null) {
                return '';
            }
            // Simple replacement - just use the value
            return String(value);
        });
        
        // Replace simple parameters
        Object.keys(params).forEach((key) => {
            const value = params[key];
            if (value !== undefined && value !== null) {
                template = template.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
            }
        });
        
        // Clean up empty select statements
        template = template.replace(/\{[^}]*\}/g, '').trim();
    }
    
    return template;
}

// Check if step is a level change (stairs, elevator, escalator)
function isStepLevelChange(step: any): boolean {
    const levelChangeTypes = ['stairs', 'elevator', 'escalator'];
    return step.type && levelChangeTypes.includes(step.type);
}

interface InstructionDetails {
    instructions: string;
    direction: string;
}

class InstructionManager {
    /**
     * Get instruction details from a step
     * Based on ItineraryUtils.getInstructionDetails
     */
    static getInstructionDetails(step: any, withStreet = true): InstructionDetails {
        if (step.currentPosition) {
            return {
                direction: 'actual-location',
                instructions: i18n('INSTRUCTION_DEPARTURE'),
            };
        }

        const street = step.name && withStreet ? step.name : '';

        let direction = step.direction || '';
        if (!direction) {
            return {
                direction: 'straight',
                instructions: this._formatInstruction('INSTRUCTION_STRAIGHT', { street }),
            };
        }

        const lastInstructions = step.lastStep ? 'INSTRUCTION_LAST_STEP' : null;
        let instructions = this._formatInstruction(lastInstructions || 'INSTRUCTION_STRAIGHT', {
            street,
            direction: i18n('INSTRUCTION_IN_FRONT'),
        });

        if (direction.includes('right')) {
            if (direction.includes('slight')) {
                direction = 'slight-right';
                instructions = this._formatInstruction(lastInstructions || 'INSTRUCTION_TURN_SLIGHTLY', {
                    street,
                    direction: i18n('INSTRUCTION_RIGHT'),
                });
            } else {
                direction = 'right';
                instructions = this._formatInstruction(lastInstructions || 'INSTRUCTION_TURN', {
                    street,
                    direction: i18n('INSTRUCTION_RIGHT'),
                });
            }
        } else if (direction.includes('left')) {
            if (direction.includes('slight')) {
                direction = 'slight-left';
                instructions = this._formatInstruction(lastInstructions || 'INSTRUCTION_TURN_SLIGHTLY', {
                    street,
                    direction: i18n('INSTRUCTION_LEFT'),
                });
            } else {
                direction = 'left';
                instructions = this._formatInstruction(lastInstructions || 'INSTRUCTION_TURN', {
                    street,
                    direction: i18n('INSTRUCTION_LEFT'),
                });
            }
        }

        if (step.type === 'gate') {
            direction = 'straight';
            instructions = this._formatInstruction('INSTRUCTION_GATE', { name: street });
        } else if (step.type === 'entrance') {
            direction = 'straight';
            instructions = this._formatInstruction('INSTRUCTION_ENTRANCE', { name: street });
        } else if (step.type === 'exit') {
            direction = 'straight';
            instructions = this._formatInstruction('INSTRUCTION_EXIT', { name: street });
        } else if (step.type === 'turnstile') {
            direction = 'straight';
            instructions = i18n('INSTRUCTION_TURNSTILE');
        } else if (isStepLevelChange(step)) {
            const directionUpper = direction.toUpperCase();
            instructions = this._formatInstruction(`INSTRUCTION_${directionUpper}`, { street });

            if (!street && step.type) {
                const typeUpper = step.type.toUpperCase();
                const specificKey = `INSTRUCTION_${directionUpper}_${typeUpper}`;
                if (LOCALES[specificKey]) {
                    instructions = i18n(specificKey);
                }
            }
            const levelStr = step.destinationLevel != null ? Level.toString(step.destinationLevel) : null;
            if (levelStr) {
                instructions += ` to level ${levelStr}`;
            }
        } else if (step.type === 'subway-entrance') {
            instructions = this._formatInstruction('INSTRUCTION_SUBWAY_EXIT', { exit: step.name });
        }

        return {
            instructions,
            direction,
        };
    }

    /**
     * Get instruction text from a step
     */
    static getInstructionFromStep(step: any): string {
        const details = this.getInstructionDetails(step);
        return details.instructions;
    }

    /**
     * Format instruction with street handling
     */
    private static _formatInstruction(key: string, params: Record<string, any>): string {
        let template = LOCALES[key] || key;
        
        // Handle street select statement: {street, select, undefined {} other {on {street}} }
        if (params.street === '' || !params.street || params.street === undefined) {
            template = template.replace(/\{street, select, undefined \{\} other \{on \{street\}\} \}/g, '');
        } else {
            template = template.replace(/\{street, select, undefined \{\} other \{on \{street\}\} \}/g, `on ${params.street}`);
        }
        
        // Handle direction select for last step: {direction, select, right {on your right} left {on your left} other {{direction}} }
        if (key === 'INSTRUCTION_LAST_STEP' && params.direction) {
            const directionValue = params.direction;
            if (directionValue === 'right' || directionValue === i18n('INSTRUCTION_RIGHT')) {
                template = template.replace(/\{direction, select, right \{on your right\} left \{on your left\} other \{\{direction\}\} \}/g, 'on your right');
            } else if (directionValue === 'left' || directionValue === i18n('INSTRUCTION_LEFT')) {
                template = template.replace(/\{direction, select, right \{on your right\} left \{on your left\} other \{\{direction\}\} \}/g, 'on your left');
            } else {
                template = template.replace(/\{direction, select, right \{on your right\} left \{on your left\} other \{\{direction\}\} \}/g, String(directionValue));
            }
        }
        
        // Handle name select for gate: {name, select, undefined {} other {{name}} }
        if (params.name !== undefined) {
            if (params.name === '' || !params.name) {
                template = template.replace(/\{name, select, undefined \{\} other \{\{name\}\} \}/g, '');
            } else {
                template = template.replace(/\{name, select, undefined \{\} other \{\{name\}\} \}/g, String(params.name));
            }
        }
        
        // Replace simple parameters (direction, exit, etc.)
        Object.keys(params).forEach((paramKey) => {
            if (paramKey !== 'street' && paramKey !== 'direction' && paramKey !== 'name') {
                const value = params[paramKey];
                if (value !== undefined && value !== null) {
                    template = template.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
                }
            }
        });
        
        // Replace direction in turn instructions (already processed, but handle standalone {direction})
        if (params.direction && key !== 'INSTRUCTION_LAST_STEP') {
            template = template.replace(/\{direction\}/g, String(params.direction));
        }
        
        // Clean up any remaining placeholders
        template = template.replace(/\{[^}]*\}/g, '').trim();
        
        return template;
    }
}

export default InstructionManager;
